import React, { useCallback, useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import {
  Row,
  Col,
  Spinner,
  Badge,
  Progress,
  Alert,
  Table,
} from "reactstrap";
import axios from "axios";
import ApexCharts from "react-apexcharts";
import Widget from "../../components/Widget/Widget.js";
import Am4chartMap from "../../components/am4chartMap/am4chartMap";
import {
  PROGRESS_BANTUAN_API,
  BANTUAN_RIWAYAT_PROGRAM_LIMA_TAHUN,
  GET_BANTUAN_TAHUN_KATEGORI,
  API_TIMEOUT,
} from "../../utils/index.js";
import { numberWithCommas } from "../../utils/func.js";
import "eva-icons/style/eva-icons.css";
import dashboardStyles from "../dashboard/Dashboard.module.scss";
import mapTableS from "../../components/am4chartMap/am4chartMap.module.scss";
import s from "./BantuanProgressDetailPage.module.scss";

const formatRupiah = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const DESIL_BAR_COLORS = [
  "#16365f",
  "#1a5084",
  "#41d5e2",
  "#ffc405",
  "#43bc13",
  "#94a3b8",
];

const APEX_FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const NIK_MASK_LAST = 8;
const maskNikTampilan = (nik) => {
  const raw = nik != null ? String(nik).replace(/\s/g, "") : "";
  if (!raw) return "—";
  const x = "x".repeat(NIK_MASK_LAST);
  if (raw.length <= NIK_MASK_LAST) return x;
  return raw.slice(0, raw.length - NIK_MASK_LAST) + x;
};

const toSmallCaps = (t) =>
  (t || "")
    .toString()
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());

const labelDesilDtsen = (desil) => {
  if (desil == null || desil === undefined || desil === "")
    return "—";
  const n = Number(desil);
  if (n === 0) return "Belum ada (DTSEN)";
  if (n >= 1 && n <= 5) return `Desil ${n}`;
  return `> Desil 5 (${n})`;
};

function BantuanProgressDetailPage({ location, history }) {
  const { tahunQ, bantuanQ } = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return {
      tahunQ: (params.get("tahun") || "").trim(),
      bantuanQ: params.get("bantuan") || "",
    };
  }, [location.search]);

  const bantuanDecoded = useMemo(() => {
    try {
      return decodeURIComponent(bantuanQ);
    } catch {
      return bantuanQ;
    }
  }, [bantuanQ]);

  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [riwayatSeries, setRiwayatSeries] = useState(null);
  const [riwayatMeta, setRiwayatMeta] = useState(null);
  const [riwayatLoading, setRiwayatLoading] = useState(false);
  const [riwayatError, setRiwayatError] = useState("");
  const [penerimaList, setPenerimaList] = useState([]);
  const [penerimaLoading, setPenerimaLoading] = useState(false);
  const [penerimaError, setPenerimaError] = useState("");

  const fetchDetail = useCallback(() => {
    if (!tahunQ || !bantuanDecoded) {
      setLoading(false);
      setDetail(null);
      setLoadError("Parameter tahun atau bantuan tidak lengkap.");
      return;
    }
    setLoading(true);
    setLoadError("");
    axios({
      method: "get",
      url: PROGRESS_BANTUAN_API + tahunQ,
      timeout: API_TIMEOUT,
    })
      .then((response) => {
        if (response.status !== 200) {
          setDetail(null);
          setLoadError("Gagal memuat data.");
          return;
        }
        const data = response.data?.data ?? [];
        const row = data.find(
          (x) =>
            String(x?.bantuan ?? "").trim() === String(bantuanDecoded).trim()
        );
        if (!row) {
          setDetail(null);
          setLoadError(
            "Data bantuan tidak ditemukan untuk tahun ini. Periksa ejaan nama bantuan."
          );
          return;
        }
        setDetail(row);
      })
      .catch(() => {
        setDetail(null);
        setLoadError("Gagal menghubungi server.");
      })
      .finally(() => setLoading(false));
  }, [tahunQ, bantuanDecoded]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    if (!detail?.bantuan || !tahunQ) {
      setRiwayatSeries(null);
      setRiwayatMeta(null);
      setRiwayatError("");
      return;
    }
    let cancelled = false;
    setRiwayatLoading(true);
    setRiwayatError("");
    axios({
      method: "get",
      url: `${BANTUAN_RIWAYAT_PROGRAM_LIMA_TAHUN}${encodeURIComponent(tahunQ)}`,
      params: { bantuan: detail.bantuan },
      timeout: API_TIMEOUT,
    })
      .then((response) => {
        if (cancelled) return;
        const payload = response.data?.data;
        if (!payload?.series || !Array.isArray(payload.series)) {
          setRiwayatSeries(null);
          setRiwayatMeta(null);
          setRiwayatError("Data riwayat tidak tersedia.");
          return;
        }
        setRiwayatSeries(payload.series);
        setRiwayatMeta({
          tahun_awal: payload.tahun_awal,
          tahun_akhir: payload.tahun_akhir,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setRiwayatSeries(null);
          setRiwayatMeta(null);
          setRiwayatError("Gagal memuat riwayat lima tahun.");
        }
      })
      .finally(() => {
        if (!cancelled) setRiwayatLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detail?.bantuan, tahunQ]);

  useEffect(() => {
    if (!detail?.bantuan || detail.tahun == null || detail.tahun === "") {
      setPenerimaList([]);
      setPenerimaError("");
      return;
    }
    let cancelled = false;
    setPenerimaLoading(true);
    setPenerimaError("");
    axios({
      method: "post",
      url: GET_BANTUAN_TAHUN_KATEGORI,
      timeout: API_TIMEOUT,
      data: { bantuan: detail.bantuan, tahun: detail.tahun },
    })
      .then((res) => {
        if (cancelled) return;
        setPenerimaList(res.data?.data ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          setPenerimaList([]);
          setPenerimaError("Gagal memuat daftar nama penerima.");
        }
      })
      .finally(() => {
        if (!cancelled) setPenerimaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detail?.bantuan, detail?.tahun]);

  const penerimaSorted = useMemo(() => {
    const rows = (penerimaList || []).filter(
      (r) => r.nama != null && String(r.nama).trim() !== ""
    );
    return [...rows].sort((a, b) => {
      const k = String(a.kecamatan || "").localeCompare(
        String(b.kecamatan || ""),
        "id"
      );
      if (k !== 0) return k;
      const d = String(a.desa || "").localeCompare(String(b.desa || ""), "id");
      if (d !== 0) return d;
      return String(a.nama || "").localeCompare(String(b.nama || ""), "id");
    });
  }, [penerimaList]);

  /* Satu grafik garis: total penerima terinput vs total desil 1 (DTSEN), satu sumbu Y. */
  const riwayatLimaTahunChart = useMemo(() => {
    if (!riwayatSeries?.length) return null;
    const cats = riwayatSeries.map((d) => String(d.tahun));
    const penerima = riwayatSeries.map((d) => Number(d.jumlah_penerima) || 0);
    const desil1 = riwayatSeries.map((d) => Number(d.total_desil1) || 0);
    return {
      series: [
        { name: "Total penerima (hasil input)", data: penerima },
        { name: "Total desil 1 (DTSEN)", data: desil1 },
      ],
      options: {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: APEX_FONT_FAMILY,
        },
        stroke: { width: [3, 2], curve: "smooth", dashArray: [0, 6] },
        colors: ["#1976d2", "#c62828"],
        markers: {
          size: 4,
          strokeWidth: 2,
          hover: { sizeOffset: 2 },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
          fontSize: "13px",
          markers: { width: 10, height: 10, radius: 2 },
        },
        xaxis: {
          categories: cats,
          labels: { style: { colors: "#6b7280" } },
          axisBorder: { show: false },
        },
        yaxis: {
          min: 0,
          title: {
            text: "Jumlah (orang)",
            style: { color: "#6b7280", fontSize: "12px" },
          },
          labels: {
            formatter: (val) =>
              Math.round(Number(val) || 0).toLocaleString("id-ID"),
            style: { colors: "#6b7280" },
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val) =>
              `${Number(val || 0).toLocaleString("id-ID")} orang`,
          },
        },
        grid: {
          borderColor: "#f1f3f5",
          strokeDashArray: 4,
          padding: { top: 8, right: 12, left: 8, bottom: 4 },
        },
      },
    };
  }, [riwayatSeries]);

  const desilRows = useMemo(() => {
    if (!detail) return [];
    const d1 = Number(detail.totalDesil1) || 0;
    const d2 = Number(detail.totalDesil2) || 0;
    const d3 = Number(detail.totalDesil3) || 0;
    const d4 = Number(detail.totalDesil4) || 0;
    const d5 = Number(detail.totalDesil5) || 0;
    const totalIn = Number(detail.jumlahpenerima) || 0;
    const above5 = Math.max(0, totalIn - d1 - d2 - d3 - d4 - d5);
    return [
      { key: "d1", label: "Desil 1", short: "D1", count: d1 },
      { key: "d2", label: "Desil 2", short: "D2", count: d2 },
      { key: "d3", label: "Desil 3", short: "D3", count: d3 },
      { key: "d4", label: "Desil 4", short: "D4", count: d4 },
      { key: "d5", label: "Desil 5", short: "D5", count: d5 },
      { key: "above5", label: "Di atas D5", short: "> D5", count: above5 },
    ];
  }, [detail]);

  const desilChart = useMemo(() => {
    const categories = desilRows.map((r) => r.short);
    const data = desilRows.map((r) => r.count);
    const totalIn = detail ? Number(detail.jumlahpenerima) || 0 : 0;
    return {
      series: [{ name: "Penerima", data }],
      options: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          zoom: { enabled: false },
        },
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: "62%",
            distributed: true,
            dataLabels: { position: "top" },
          },
        },
        colors: DESIL_BAR_COLORS,
        dataLabels: {
          enabled: true,
          offsetY: -18,
          style: {
            fontSize: "11px",
            colors: ["#304758"],
            fontWeight: 600,
          },
          formatter: (val) =>
            val > 0 ? Number(val).toLocaleString("id-ID") : "",
        },
        legend: { show: false },
        xaxis: {
          categories,
          labels: {
            style: { colors: "#6b7280", fontSize: "12px", fontWeight: 500 },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          title: {
            text: "Jumlah penerima",
            style: { color: "#6b7280", fontSize: "12px", fontWeight: 500 },
          },
          labels: {
            formatter: (val) =>
              Math.round(val).toLocaleString("id-ID"),
            style: { colors: "#6b7280" },
          },
        },
        grid: {
          borderColor: "#f1f3f5",
          strokeDashArray: 4,
          padding: { top: 8, right: 8, left: 12, bottom: 4 },
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: (val) => {
              const pct =
                totalIn > 0
                  ? ((Number(val) / totalIn) * 100).toFixed(1)
                  : "0";
              return `${val.toLocaleString("id-ID")} orang (${pct}% dari terinput)`;
            },
          },
        },
        subtitle: {
          text:
            detail && totalIn
              ? `Berdasarkan ${totalIn.toLocaleString(
                  "id-ID"
                )} penerima terinput (DTSEN)`
              : "",
          align: "left",
          style: { fontSize: "12px", color: "#6b7280" },
        },
      },
    };
  }, [desilRows, detail]);

  const pctInput =
    detail && detail.jmlPenerima
      ? Math.round(
          (Number(detail.jumlahpenerima) / Number(detail.jmlPenerima)) * 100
        )
      : 0;

  const pctInputTarget = Math.min(100, Math.max(0, pctInput));

  const d = dashboardStyles;

  return (
    <div className={s.page}>
      <button
        type="button"
        className={s.backLink}
        onClick={() => history.push("/template/progressinput/onprogress")}
      >
        <span className={s.backIcon} aria-hidden="true">
          ←
        </span>
        On progress
      </button>

      {loading && (
        <div className={s.loader}>
          <Spinner color="secondary" size="sm" />
          <span>Memuat detail…</span>
        </div>
      )}

      {!loading && loadError && (
        <div className={s.errorBox} role="alert">
          {loadError}
        </div>
      )}

      {!loading && detail && (
        <>
          <div className={d.summaryRekapanCard}>
            <div className={`${d.summaryRekapanHeader} ${s.detailRekapanHeader}`}>
              <h4 className={`${d.summaryRekapanTitle} ${s.detailRekapanTitle}`}>
                {detail.bantuan}
              </h4>
              <div className={s.headerChips}>
                <span className={s.metaChip}>Tahun {detail.tahun}</span>
                {detail.dinas ? (
                  <span className={s.metaChipMuted}>{detail.dinas}</span>
                ) : null}
              </div>
            </div>

            <div className={d.summaryRekapanGrid}>
              <div className={`${d.summaryItem} ${d.summaryItemRed}`}>
                <span className={d.summaryIconWrap}>
                  <i className="eva eva-checkmark-circle-outline eva-lg" />
                </span>
                <div className={d.summaryContent}>
                  <span className={d.summaryLabel}>Target (SK)</span>
                  <span className={d.summaryValue}>
                    {numberWithCommas(detail.jmlPenerima)}
                  </span>
                </div>
              </div>
              <div
                className={`${d.summaryItem} ${d.summaryItemHighlight} ${d.summaryItemBlue} ${s.summaryItemAlignStart}`}
              >
                <span className={`${d.summaryIconWrap} ${s.iconAlignTop}`}>
                  <i className="eva eva-person-done-outline eva-lg" />
                </span>
                <div className={`${d.summaryContent} ${s.terinputContent}`}>
                  <span className={d.summaryLabel}>Terinput</span>
                  <span className={d.summaryValue}>
                    {numberWithCommas(detail.jumlahpenerima)}
                    <span className={s.pctInline}> ({pctInput}%)</span>
                  </span>
                  <Progress
                    value={pctInputTarget}
                    className={s.cardProgressBar}
                    color={pctInput >= 50 ? "success" : "warning"}
                  />
                </div>
              </div>
              <div className={`${d.summaryItem} ${d.summaryItemBlue}`}>
                <span className={d.summaryIconWrap}>
                  <i className="eva eva-credit-card-outline eva-lg" />
                </span>
                <div className={d.summaryContent}>
                  <span className={d.summaryLabel}>Total nilai</span>
                  <span className={d.summaryValue}>
                    {formatRupiah(detail.totalUang)}
                  </span>
                </div>
              </div>
              <div className={`${d.summaryItem} ${d.summaryItemGreen}`}>
                <span className={d.summaryIconWrap}>
                  <i className="eva eva-heart-outline eva-lg" />
                </span>
                <div className={d.summaryContent}>
                  <span className={d.summaryLabel}>Desil 1 (ME)</span>
                  <span className={d.summaryValue}>
                    {numberWithCommas(
                      detail.jumlahmiskinekstrim ?? detail.totalDesil1 ?? 0
                    )}
                  </span>
                </div>
              </div>
            </div>

            {(detail.nomorSK || detail.tanggalSK || detail.tanggalBuka) && (
              <div className={s.docRow}>
                {detail.nomorSK ? (
                  <Badge className={s.docBadge}>SK {detail.nomorSK}</Badge>
                ) : null}
                {detail.tanggalSK ? (
                  <Badge className={s.docBadge}>Tgl SK {detail.tanggalSK}</Badge>
                ) : null}
                {detail.tanggalBuka ? (
                  <Badge className={s.docBadge}>
                    Buka input {detail.tanggalBuka}
                  </Badge>
                ) : null}
              </div>
            )}
          </div>

          <Row className={`gutter mb-4 ${s.mainRow}`}>
            <Col xs={12}>
              <Widget
                style={{ padding: 20 }}
                className={`${dashboardStyles.chartPenerimaDesil} ${s.chartWidget}`}
              >
                <div className={s.chartTitle}>Penerima bantuan per desil</div>
                <p className={s.chartCaption}>
                  Grafik batang membandingkan jumlah penerima tiap kategori
                  desil (sumber DTSEN, hanya yang sudah terinput).
                </p>
                <div className={s.chartMount}>
                  <ApexCharts
                    key={`${tahunQ}-${detail.bantuan}`}
                    options={desilChart.options}
                    series={desilChart.series}
                    type="bar"
                    height={320}
                  />
                </div>
              </Widget>
            </Col>
          </Row>

          <Row className={`gutter mb-4 ${s.mainRow}`}>
            <Col xs={12}>
              <Widget
                style={{ padding: 20 }}
                className={`${dashboardStyles.chartPenerimaDesil} ${s.chartWidget} ${s.riwayatWidget}`}
              >
                <div className={s.chartTitle}>Program yang sama — lima tahun terakhir</div>
                <p className={s.chartCaption}>
                  Tren program dengan nama yang sama ({riwayatMeta?.tahun_awal ?? "—"} –{" "}
                  {riwayatMeta?.tahun_akhir ?? tahunQ}):{" "}
                  <strong>Total penerima (hasil input)</strong> dari databantuan, dan{" "}
                  <strong>total desil 1</strong> (maks. desil nasional DTSEN = 1) pada
                  penerima yang sama.
                </p>
                {riwayatLoading && (
                  <div className={s.riwayatLoader}>
                    <Spinner color="secondary" size="sm" />
                    <span>Memuat riwayat…</span>
                  </div>
                )}
                {!riwayatLoading && riwayatError && (
                  <Alert color="warning" className="mb-0 py-2 small">
                    {riwayatError}
                  </Alert>
                )}
                {!riwayatLoading && riwayatLimaTahunChart && (
                  <div className={s.chartMount}>
                    <ApexCharts
                      key={`riwayat-5th-${tahunQ}-${detail.bantuan}`}
                      options={riwayatLimaTahunChart.options}
                      series={riwayatLimaTahunChart.series}
                      type="line"
                      height={320}
                    />
                  </div>
                )}
              </Widget>
            </Col>
          </Row>

          <Row className={`gutter ${s.mainRow}`}>
            <Col xs={12}>
              <Widget style={{ padding: 20 }} className={s.mapWidget}>
                <div className={`${s.chartTitle} ${s.mapSectionTitle}`}>
                  Sebaran per desa
                </div>
                <Am4chartMap
                  tahun={String(tahunQ)}
                  fixedBantuan={detail.bantuan}
                  compactHeader
                  hidePerDesaTable
                />
                <div className={mapTableS.mapDataTableWrap}>
                  <h6 className={mapTableS.mapDataTableTitle}>
                    Daftar nama penerima bantuan — Tahun {detail.tahun}
                  </h6>
                  {penerimaLoading && (
                    <div className={s.mapTableMessage}>
                      <Spinner color="secondary" size="sm" className="mr-2" />
                      Memuat data penerima…
                    </div>
                  )}
                  {!penerimaLoading && penerimaError && (
                    <div className={s.mapTableMessage}>
                      <Alert color="warning" className="py-2 small mb-0">
                        {penerimaError}
                      </Alert>
                    </div>
                  )}
                  {!penerimaLoading &&
                    !penerimaError &&
                    penerimaSorted.length === 0 && (
                      <div className={s.mapTableMessage}>
                        Belum ada penerima dengan nama terisi.
                      </div>
                    )}
                  {!penerimaLoading &&
                    !penerimaError &&
                    penerimaSorted.length > 0 && (
                      <div className={mapTableS.mapDataTableScroll}>
                        <Table
                          responsive
                          bordered
                          size="sm"
                          className={mapTableS.mapDataTable}
                        >
                          <thead>
                            <tr>
                              <th className={mapTableS.thNo}>No</th>
                              <th>Nama penerima</th>
                              <th>NIK</th>
                              <th>Desa</th>
                              <th>Kecamatan</th>
                              <th>Desil (DTSEN)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {penerimaSorted.map((row, idx) => (
                              <tr
                                key={
                                  row.id != null
                                    ? `pn-${row.id}`
                                    : `pn-${row.nik}-${idx}`
                                }
                              >
                                <td className={mapTableS.thNo}>{idx + 1}</td>
                                <td>{toSmallCaps(row.nama)}</td>
                                <td className={mapTableS.thNum}>
                                  {maskNikTampilan(row.nik)}
                                </td>
                                <td>{toSmallCaps(row.desa)}</td>
                                <td>{toSmallCaps(row.kecamatan)}</td>
                                <td className={mapTableS.thNum}>
                                  {labelDesilDtsen(row.desil_nasional)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                </div>
              </Widget>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

export default withRouter(BantuanProgressDetailPage);
